import type { JSX } from 'react';

import type { WeekGrid, WeekGridCellBreakdown } from '@asas/domain';

import { hinweisText, stundenAnzeige, wochenkopf, zellenText, zellenzustand } from './wochenraster';

/**
 * Die Darstellung des Wochenrasters (AC-402) — ohne Zustand und ohne Netz.
 * Mitarbeiter in Zeilen, Kalenderwochen in Spalten (AK 1). Die Tabelle selbst
 * ist nicht editierbar: keine Eingabefelder, keine Ziehgriffe. Eine offene
 * Zelle ist ein Knopf, der ihre Aufschluesselung anfordert (AK 2) — bzw. mit
 * `resource_allocation:write` ihre bearbeitbaren Belegungen (`ZellePlanen`,
 * AC-403).
 */

export interface ZellenAuswahl {
  readonly employeeId: string;
  readonly isoYear: number;
  readonly isoWeek: number;
}

export interface WochenrasterTabelleProps {
  readonly raster: WeekGrid;
  readonly ausgewaehlt: ZellenAuswahl | null;
  /** Waehrend geladen wird, fordert ein Klick nichts an. */
  readonly gesperrt: boolean;
  readonly onZelle: (auswahl: ZellenAuswahl) => void;
}

export function WochenrasterTabelle({
  raster,
  ausgewaehlt,
  gesperrt,
  onZelle,
}: WochenrasterTabelleProps): JSX.Element {
  return (
    <>
      {raster.hinweis === null ? null : <p>{hinweisText(raster.hinweis)}</p>}
      <table>
        <caption>Planstunden je Woche / verfügbare Stunden</caption>
        <thead>
          <tr>
            <th scope="col">Mitarbeiter</th>
            {raster.wochen.map((woche, index) => (
              <th scope="col" key={`${String(woche.isoYear)}-${String(woche.isoWeek)}`}>
                {wochenkopf(woche, raster.wochen[index - 1])}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {raster.zeilen.map((zeile) => (
            <tr key={zeile.employeeId}>
              <th scope="row">{zeile.name}</th>
              {zeile.zellen.map((zelle) => {
                const zustand = zellenzustand(zelle);
                const key = `${String(zelle.isoYear)}-${String(zelle.isoWeek)}`;

                if (zelle.locked) {
                  return (
                    <td key={key} data-zustand={zustand}>
                      {zellenText(zelle)}
                    </td>
                  );
                }

                const istAusgewaehlt =
                  ausgewaehlt?.employeeId === zeile.employeeId &&
                  ausgewaehlt.isoYear === zelle.isoYear &&
                  ausgewaehlt.isoWeek === zelle.isoWeek;

                return (
                  <td key={key} data-zustand={zustand}>
                    <button
                      type="button"
                      aria-pressed={istAusgewaehlt}
                      disabled={gesperrt}
                      onClick={() => {
                        onZelle({
                          employeeId: zeile.employeeId,
                          isoYear: zelle.isoYear,
                          isoWeek: zelle.isoWeek,
                        });
                      }}
                    >
                      {zellenText(zelle)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export interface AufschluesselungProps {
  readonly name: string;
  readonly aufschluesselung: WeekGridCellBreakdown;
}

/** Die Aufschluesselung einer Zelle je Projekt (AK 2); nicht sichtbare als eine Summe (AK 5). */
export function Aufschluesselung({ name, aufschluesselung }: AufschluesselungProps): JSX.Element {
  const titel = `${name}, KW ${String(aufschluesselung.isoWeek)}/${String(aufschluesselung.isoYear)}`;

  if (aufschluesselung.projekte.length === 0) {
    return (
      <section aria-label="Aufschlüsselung">
        <h3>{titel}</h3>
        <p>Keine eingeplanten Stunden.</p>
      </section>
    );
  }

  return (
    <section aria-label="Aufschlüsselung">
      <h3>{titel}</h3>
      <ul>
        {aufschluesselung.projekte.map((projekt) => (
          <li key={projekt.projectId ?? 'anonymisiert'}>
            {projekt.projectLabel}: {stundenAnzeige(projekt.hours)} h
            {projekt.anonymized ? ' (anonymisiert)' : ''}
          </li>
        ))}
      </ul>
    </section>
  );
}
